'use client';

import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  setCurrentLocation,
  setSelectedStrategy,
  setSelectedTerritory,
  setSelectedFilterSet,
  setSelectedProviders,
  setSelectedProvider,
  setBoundedTerritory,
  setActiveMarker,
  setZoom,
  setCenter,
  setBounds,
  setSelectedCities,
  setSelectedStates,
  setSelectedSpecialties,
  setSelectedSocTypes,
  setSelectedSocSubtypes,
  setSelectedStatuses,
  setSelectedProviderLists,
  setSelectedChainIds,
  setMetricToggle,
  setShowHeatmap,
  setSelectedHeatmapMetric,
  setIsAllSelected,
  setView,
} from 'src/redux';
import { initialLocationState } from 'src/redux/slices/strategySlice';
import type { RootState } from 'src/redux';

interface UseStrategiesReturn {
  selectedStrategy: MedScout.ChainedStrategy | null;
  selectedTerritory: MedScout.Territory | null;
  selectedFilterSet: MedScout.FilterSet | null;
  selectedProviders: MedScout.Opportunity[] | null;
  selectedProvider: MedScout.Opportunity | null;
  boundedTerritory: MedScout.Territory | null;
  activeMarker: string | null;
  zoom: number;
  center: {
    lat: number;
    lng: number;
  };
  bounds: {
    min_lat: number;
    max_lat: number;
    min_lng: number;
    max_lng: number;
  };
  selectedCities: string[];
  selectedStates: string[];
  selectedSpecialties: string[];
  selectedSocTypes: string[];
  selectedSocSubtypes: string[];
  selectedStatuses: string[];
  selectedProviderLists: string[];
  selectedChainIds: number[];
  metricToggles: Record<string, string>;
  showHeatmap: boolean;
  selectedHeatmapMetric: { code: string; label: string } | null;
  setSelectedStrategy: (strategy: MedScout.ChainedStrategy | null) => void;
  setSelectedTerritory: (territory: MedScout.Territory) => void;
  setSelectedFilterSet: (filterSet: MedScout.FilterSet) => void;
  setSelectedProviders: (providers: MedScout.Opportunity[]) => void;
  setSelectedProvider: (provider: MedScout.Opportunity | null) => void;
  clearSelectedProviders: () => void;
  setBoundedTerritory: (territory: MedScout.Territory) => void;
  setActiveMarker: (markerId: string | null) => void;
  setZoom: (zoom: number) => void;
  setCenter: (center: { lat: number; lng: number }) => void;
  setBounds: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  setSelectedCities: (cities: string[]) => void;
  setSelectedStates: (states: string[]) => void;
  setSelectedSpecialties: (specialties: string[]) => void;
  setSelectedSocTypes: (socTypes: string[]) => void;
  setSelectedSocSubtypes: (socSubtypes: string[]) => void;
  setSelectedStatuses: (statuses: string[]) => void;
  setSelectedProviderLists: (providerLists: string[]) => void;
  clearSelectedCities: () => void;
  clearSelectedStates: () => void;
  clearSelectedSpecialties: () => void;
  clearSelectedSocTypes: () => void;
  clearSelectedSocSubtypes: () => void;
  clearSelectedStatuses: () => void;
  clearSelectedProviderLists: () => void;
  setSelectedChainIds: (chainIds: number[]) => void;
  setMetricToggle: (metricId: string, option: string) => void;
  setShowHeatmap: (show: boolean) => void;
  setSelectedHeatmapMetric: (
    metric: { code: string; label: string } | null
  ) => void;
  isAllSelected: boolean;
  setIsAllSelected: (value: boolean) => void;
  view: 'map' | 'list';
  setView: (view: 'map' | 'list') => void;
}

const useStrategies = (location: string): UseStrategiesReturn => {
  const dispatch = useDispatch();

  // Read the entire location state in one selector with shallowEqual
  const locationState = useSelector(
    (state: RootState) =>
      state.strategies.locations[location] || initialLocationState,
    shallowEqual
  );

  // Helper: ensure currentLocation is set before dispatching a write action
  const dispatchWithLocation = useCallback(
    (action: any) => {
      dispatch(setCurrentLocation(location));
      dispatch(action);
    },
    [dispatch, location]
  );

  const setSelectedStrategyAction = useCallback(
    (strategy: MedScout.ChainedStrategy | null) => {
      dispatchWithLocation(setSelectedStrategy(strategy));
    },
    [dispatchWithLocation]
  );

  const setSelectedTerritoryAction = useCallback(
    (territory: MedScout.Territory) => {
      dispatchWithLocation(setSelectedTerritory(territory));
    },
    [dispatchWithLocation]
  );

  const setSelectedFilterSetAction = useCallback(
    (filterSet: MedScout.FilterSet) => {
      dispatchWithLocation(setSelectedFilterSet(filterSet));
    },
    [dispatchWithLocation]
  );

  const setSelectedProvidersAction = useCallback(
    (providers: MedScout.Opportunity[]) => {
      dispatchWithLocation(setSelectedProviders(providers));
    },
    [dispatchWithLocation]
  );

  const setSelectedProviderAction = useCallback(
    (provider: MedScout.Opportunity | null) => {
      dispatchWithLocation(setSelectedProvider(provider));
    },
    [dispatchWithLocation]
  );

  const clearSelectedProvidersAction = useCallback(() => {
    dispatchWithLocation(setSelectedProviders(null));
  }, [dispatchWithLocation]);

  const setBoundedTerritoryAction = useCallback(
    (territory: MedScout.Territory) => {
      dispatchWithLocation(setBoundedTerritory(territory));
    },
    [dispatchWithLocation]
  );

  const setActiveMarkerAction = useCallback(
    (markerId: string | null) => {
      dispatchWithLocation(setActiveMarker(markerId));
    },
    [dispatchWithLocation]
  );

  const setZoomAction = useCallback(
    (zoom: number) => {
      dispatchWithLocation(setZoom(zoom));
    },
    [dispatchWithLocation]
  );

  const setCenterAction = useCallback(
    (center: { lat: number; lng: number }) => {
      dispatchWithLocation(setCenter(center));
    },
    [dispatchWithLocation]
  );

  const setBoundsAction = useCallback(
    (bounds: { north: number; south: number; east: number; west: number }) => {
      dispatchWithLocation(setBounds(bounds));
    },
    [dispatchWithLocation]
  );

  const setSelectedCitiesAction = useCallback(
    (cities: string[]) => {
      dispatchWithLocation(setSelectedCities(cities));
    },
    [dispatchWithLocation]
  );

  const setSelectedStatesAction = useCallback(
    (states: string[]) => {
      dispatchWithLocation(setSelectedStates(states));
    },
    [dispatchWithLocation]
  );

  const setSelectedSpecialtiesAction = useCallback(
    (specialties: string[]) => {
      dispatchWithLocation(setSelectedSpecialties(specialties));
    },
    [dispatchWithLocation]
  );

  const setSelectedSocTypesAction = useCallback(
    (socTypes: string[]) => {
      dispatchWithLocation(setSelectedSocTypes(socTypes));
    },
    [dispatchWithLocation]
  );

  const setSelectedSocSubtypesAction = useCallback(
    (socSubtypes: string[]) => {
      dispatchWithLocation(setSelectedSocSubtypes(socSubtypes));
    },
    [dispatchWithLocation]
  );

  const setSelectedStatusesAction = useCallback(
    (statuses: string[]) => {
      dispatchWithLocation(setSelectedStatuses(statuses));
    },
    [dispatchWithLocation]
  );

  const setSelectedProviderListsAction = useCallback(
    (providerLists: string[]) => {
      dispatchWithLocation(setSelectedProviderLists(providerLists));
    },
    [dispatchWithLocation]
  );

  const clearSelectedCitiesAction = useCallback(() => {
    dispatchWithLocation(setSelectedCities([]));
  }, [dispatchWithLocation]);

  const clearSelectedStatesAction = useCallback(() => {
    dispatchWithLocation(setSelectedStates([]));
  }, [dispatchWithLocation]);

  const clearSelectedSpecialtiesAction = useCallback(() => {
    dispatchWithLocation(setSelectedSpecialties([]));
  }, [dispatchWithLocation]);

  const clearSelectedSocTypesAction = useCallback(() => {
    dispatchWithLocation(setSelectedSocTypes([]));
  }, [dispatchWithLocation]);

  const clearSelectedSocSubtypesAction = useCallback(() => {
    dispatchWithLocation(setSelectedSocSubtypes([]));
  }, [dispatchWithLocation]);

  const clearSelectedStatusesAction = useCallback(() => {
    dispatchWithLocation(setSelectedStatuses([]));
  }, [dispatchWithLocation]);

  const clearSelectedProviderListsAction = useCallback(() => {
    dispatchWithLocation(setSelectedProviderLists([]));
  }, [dispatchWithLocation]);

  const setSelectedChainIdsAction = useCallback(
    (chainIds: number[]) => {
      dispatchWithLocation(setSelectedChainIds(chainIds));
    },
    [dispatchWithLocation]
  );

  const setMetricToggleAction = useCallback(
    (metricId: string, option: string) => {
      dispatchWithLocation(setMetricToggle({ metricId, option }));
    },
    [dispatchWithLocation]
  );

  const setShowHeatmapAction = useCallback(
    (show: boolean) => {
      dispatchWithLocation(setShowHeatmap(show));
    },
    [dispatchWithLocation]
  );

  const setSelectedHeatmapMetricAction = useCallback(
    (metric: { code: string; label: string } | null) => {
      dispatchWithLocation(setSelectedHeatmapMetric(metric));
    },
    [dispatchWithLocation]
  );

  const setIsAllSelectedAction = useCallback(
    (value: boolean) => {
      dispatchWithLocation(setIsAllSelected(value));
    },
    [dispatchWithLocation]
  );

  const setViewAction = useCallback(
    (view: 'map' | 'list') => {
      dispatchWithLocation(setView(view));
    },
    [dispatchWithLocation]
  );

  const selectedChainIds = useMemo(
    () => locationState.selectedChainIds ?? [],
    [locationState.selectedChainIds]
  );

  const selectedSocTypes = useMemo(
    () => locationState.selectedSocTypes ?? [],
    [locationState.selectedSocTypes]
  );

  const selectedSocSubtypes = useMemo(
    () => locationState.selectedSocSubtypes ?? [],
    [locationState.selectedSocSubtypes]
  );

  const metricToggles = useMemo(
    () => locationState.metricToggles || {},
    [locationState.metricToggles]
  );

  return {
    selectedStrategy: locationState.selectedStrategy,
    selectedTerritory: locationState.selectedTerritory,
    selectedFilterSet: locationState.selectedFilterSet,
    selectedProviders: locationState.selectedProviders,
    selectedProvider: locationState.selectedProvider,
    boundedTerritory: locationState.boundedTerritory,
    activeMarker: locationState.activeMarker,
    zoom: locationState.zoom,
    center: locationState.center,
    bounds: locationState.bounds,
    selectedCities: locationState.selectedCities,
    selectedStates: locationState.selectedStates,
    selectedSpecialties: locationState.selectedSpecialties,
    selectedSocTypes,
    selectedSocSubtypes,
    selectedStatuses: locationState.selectedStatuses,
    selectedProviderLists: locationState.selectedProviderLists,
    selectedChainIds,
    metricToggles,
    showHeatmap: locationState.showHeatmap ?? false,
    selectedHeatmapMetric: locationState.selectedHeatmapMetric ?? null,
    setSelectedStrategy: setSelectedStrategyAction,
    setSelectedTerritory: setSelectedTerritoryAction,
    setSelectedFilterSet: setSelectedFilterSetAction,
    setSelectedProviders: setSelectedProvidersAction,
    setSelectedProvider: setSelectedProviderAction,
    clearSelectedProviders: clearSelectedProvidersAction,
    setBoundedTerritory: setBoundedTerritoryAction,
    setActiveMarker: setActiveMarkerAction,
    setZoom: setZoomAction,
    setCenter: setCenterAction,
    setBounds: setBoundsAction,
    setSelectedCities: setSelectedCitiesAction,
    setSelectedStates: setSelectedStatesAction,
    setSelectedSpecialties: setSelectedSpecialtiesAction,
    setSelectedSocTypes: setSelectedSocTypesAction,
    setSelectedSocSubtypes: setSelectedSocSubtypesAction,
    setSelectedStatuses: setSelectedStatusesAction,
    setSelectedProviderLists: setSelectedProviderListsAction,
    clearSelectedCities: clearSelectedCitiesAction,
    clearSelectedStates: clearSelectedStatesAction,
    clearSelectedSpecialties: clearSelectedSpecialtiesAction,
    clearSelectedSocTypes: clearSelectedSocTypesAction,
    clearSelectedSocSubtypes: clearSelectedSocSubtypesAction,
    clearSelectedStatuses: clearSelectedStatusesAction,
    clearSelectedProviderLists: clearSelectedProviderListsAction,
    setSelectedChainIds: setSelectedChainIdsAction,
    setMetricToggle: setMetricToggleAction,
    setShowHeatmap: setShowHeatmapAction,
    setSelectedHeatmapMetric: setSelectedHeatmapMetricAction,
    isAllSelected: locationState.isAllSelected ?? false,
    setIsAllSelected: setIsAllSelectedAction,
    view: locationState.view ?? 'list',
    setView: setViewAction,
  };
};

export default useStrategies;
